/**
 * Login Form Component
 * Email/password authentication with validation
 */

import { useState, FormEvent } from 'react';
import { useSignIn } from '@clerk/clerk-react';
import { Button } from '@/ui/components/ui/button';
import { Input } from '@/ui/components/ui/input';
import { Label } from '@/ui/components/ui/label';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { AuthMode } from '@/dto';

interface LoginFormProps {
  mode: AuthMode;
  onSubmit: (email: string, password?: string) => Promise<void>;
  onModeChange?: (mode: AuthMode) => void;
}

// Email validation regex (RFC 5322 simplified)
const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

export function LoginForm({ mode, onSubmit, onModeChange }: LoginFormProps) {
  const { signIn } = useSignIn();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const validateEmail = (email: string): boolean => {
    if (!email) {
      setEmailError('Email is required');
      return false;
    }
    if (!EMAIL_REGEX.test(email)) {
      setEmailError('Please enter a valid email address');
      return false;
    }
    setEmailError(null);
    return true;
  };

  const validatePassword = (password: string): boolean => {
    if (mode === AuthMode.EMAIL_LINK) {
      return true; // No password needed for email link
    }
    if (!password) {
      setPasswordError('Password is required');
      return false;
    }
    if (password.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return false;
    }
    setPasswordError(null);
    return true;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate inputs
    const isEmailValid = validateEmail(email);
    const isPasswordValid = validatePassword(password);

    if (!isEmailValid || (mode === AuthMode.PASSWORD && !isPasswordValid)) {
      return;
    }

    setIsLoading(true);

    try {
      if (mode === AuthMode.EMAIL_LINK) {
        await onSubmit(email);
      } else {
        await onSubmit(email, password);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Authentication failed');
    } finally {
      setIsLoading(false);
    }
  };

  const getSubmitButtonText = (): string => {
    if (isLoading) return 'Please wait...';
    switch (mode) {
      case AuthMode.EMAIL_LINK:
        return 'Send Sign-In Link';
      case AuthMode.PASSWORD_SETUP:
        return 'Set Password';
      default:
        return 'Sign In';
    }
  };

  const handleGoogleSignIn = async () => {
    console.log('Google sign-in clicked');
    console.log('signIn available:', !!signIn);

    if (!signIn) {
      console.error('Clerk signIn not available');
      setError('Authentication service not ready. Please refresh the page.');
      return;
    }

    try {
      console.log('Starting OAuth redirect...');
      await signIn.authenticateWithRedirect({
        strategy: 'oauth_google',
        redirectUrl: window.location.origin + '/dashboard',
        redirectUrlComplete: '/dashboard',
      });
      console.log('Redirect initiated');
    } catch (error) {
      console.error('Google sign-in error:', error);
      setError('Failed to sign in with Google. Please try again.');
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Error Banner */}
      {error && (
        <div
          style={{
            background: 'rgba(142, 50, 70, 0.05)',
            border: '1px solid rgba(142, 50, 70, 0.3)',
            borderRadius: '8px',
            padding: '12px',
            color: '#8E3246',
            fontSize: '14px',
            lineHeight: 1.6
          }}
        >
          {error}
        </div>
      )}

      {/* Email Field */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Label
          htmlFor="email"
          style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: '14px',
            fontWeight: 500,
            color: '#5C4033',
            letterSpacing: '0.1px'
          }}
        >
          Email
        </Label>
        <Input
          id="email"
          type="email"
          placeholder="Enter your email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setEmailError(null);
          }}
          style={{
            backgroundColor: '#FFFFFF',
            border: emailError ? '1.5px solid #8E3246' : '1px solid #CDBCA5',
            borderRadius: '10px',
            padding: '12px 18px',
            fontSize: '15px',
            color: '#4F3831',
            fontFamily: "'Inter', sans-serif",
            lineHeight: 1.6,
            outline: 'none',
            transition: 'border-color 200ms ease'
          }}
          disabled={isLoading}
          autoComplete="email"
          required
          onFocus={(e) => {
            if (!emailError) {
              e.target.style.border = '1.5px solid #9333EA';
            }
          }}
          onBlur={(e) => {
            validateEmail(email);
            if (!emailError) {
              e.target.style.border = '1px solid #CDBCA5';
            }
          }}
        />
        {emailError && (
          <p
            style={{
              fontSize: '12px',
              fontWeight: 500,
              color: '#8E3246',
              letterSpacing: '0.2px',
              marginTop: '4px'
            }}
          >
            {emailError}
          </p>
        )}
      </div>

      {/* Password Field (hidden for email link mode) */}
      {mode !== AuthMode.EMAIL_LINK && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Label
            htmlFor="password"
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: '14px',
              fontWeight: 500,
              color: '#5C4033',
              letterSpacing: '0.1px'
            }}
          >
            Password
          </Label>
          <div style={{ position: 'relative' }}>
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter your password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setPasswordError(null);
              }}
              style={{
                backgroundColor: '#FFFFFF',
                border: passwordError ? '1.5px solid #8E3246' : '1px solid #CDBCA5',
                borderRadius: '10px',
                padding: '12px 18px',
                paddingRight: '45px',
                fontSize: '15px',
                color: '#4F3831',
                fontFamily: "'Inter', sans-serif",
                lineHeight: 1.6,
                outline: 'none',
                transition: 'border-color 200ms ease',
                width: '100%'
              }}
              disabled={isLoading}
              autoComplete={mode === AuthMode.PASSWORD_SETUP ? 'new-password' : 'current-password'}
              required
              onFocus={(e) => {
                if (!passwordError) {
                  e.target.style.border = '1.5px solid #9333EA';
                }
              }}
              onBlur={(e) => {
                validatePassword(password);
                if (!passwordError) {
                  e.target.style.border = '1px solid #CDBCA5';
                }
              }}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                color: '#4F3831'
              }}
              tabIndex={-1}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {passwordError && (
            <p
              style={{
                fontSize: '12px',
                fontWeight: 500,
                color: '#8E3246',
                letterSpacing: '0.2px',
                marginTop: '4px'
              }}
            >
              {passwordError}
            </p>
          )}
        </div>
      )}

      {/* Submit Button */}
      <Button
        type="submit"
        disabled={isLoading}
        style={{
          width: '100%',
          height: '48px',
          backgroundColor: isLoading ? 'rgba(147, 51, 234, 0.1)' : '#9333EA',
          color: isLoading ? '#9333EA' : '#FFFFFF',
          border: isLoading ? '2px solid #9333EA' : 'none',
          borderRadius: '8px',
          padding: '10px 18px',
          fontSize: '15px',
          fontWeight: 500,
          fontFamily: "'Inter', sans-serif",
          letterSpacing: '0.5px',
          cursor: isLoading ? 'not-allowed' : 'pointer',
          transition: 'all 300ms ease-in-out',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px'
        }}
        onMouseEnter={(e) => {
          if (!isLoading) {
            e.currentTarget.style.backgroundColor = '#7C3AED';
            e.currentTarget.style.boxShadow = '0 2px 6px rgba(147, 51, 234, 0.3)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isLoading) {
            e.currentTarget.style.backgroundColor = '#9333EA';
            e.currentTarget.style.boxShadow = 'none';
          }
        }}
      >
        {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
        {getSubmitButtonText()}
      </Button>

      {/* Divider */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '8px 0' }}>
        <div style={{ flex: 1, height: '1px', backgroundColor: '#CDBCA5' }} />
        <span style={{ fontSize: '12px', color: 'rgba(92, 64, 51, 0.6)', fontFamily: "'Inter', sans-serif" }}>
          OR
        </span>
        <div style={{ flex: 1, height: '1px', backgroundColor: '#CDBCA5' }} />
      </div>

      {/* Google Sign-In Button */}
      <Button
        type="button"
        onClick={handleGoogleSignIn}
        disabled={isLoading}
        style={{
          width: '100%',
          height: '48px',
          backgroundColor: '#FFFFFF',
          color: '#4F3831',
          border: '1px solid #CDBCA5',
          borderRadius: '8px',
          padding: '10px 18px',
          fontSize: '15px',
          fontWeight: 500,
          fontFamily: "'Inter', sans-serif",
          letterSpacing: '0.5px',
          cursor: isLoading ? 'not-allowed' : 'pointer',
          transition: 'all 300ms ease-in-out',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px'
        }}
        onMouseEnter={(e) => {
          if (!isLoading) {
            e.currentTarget.style.backgroundColor = '#F6EDE8';
            e.currentTarget.style.boxShadow = '0 2px 6px rgba(79, 56, 49, 0.15)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isLoading) {
            e.currentTarget.style.backgroundColor = '#FFFFFF';
            e.currentTarget.style.boxShadow = 'none';
          }
        }}
      >
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
          <path d="M9.003 18c2.43 0 4.467-.806 5.956-2.18L12.05 13.56c-.806.54-1.836.86-3.047.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9.003 18z" fill="#34A853"/>
          <path d="M3.964 10.712c-.18-.54-.282-1.117-.282-1.71 0-.593.102-1.17.282-1.71V4.96H.957C.347 6.175 0 7.55 0 9.002c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
          <path d="M9.003 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.464.891 11.427 0 9.002 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29c.708-2.127 2.692-3.71 5.036-3.71z" fill="#EA4335"/>
        </svg>
        Continue with Google
      </Button>

      {/* Mode Switching Links */}
      {onModeChange && mode === AuthMode.PASSWORD && (
        <div className="space-y-2 text-sm text-center">
          <button
            type="button"
            onClick={() => onModeChange(AuthMode.EMAIL_LINK)}
            className="text-purple-600 hover:underline block w-full"
            disabled={isLoading}
          >
            Sign in with email link instead
          </button>
          <button
            type="button"
            onClick={() => {
              /* Navigate to password reset */
            }}
            className="text-gray-600 hover:underline block w-full"
            disabled={isLoading}
          >
            Forgot password?
          </button>
        </div>
      )}

      {mode === AuthMode.EMAIL_LINK && onModeChange && (
        <div className="text-sm text-center">
          <button
            type="button"
            onClick={() => onModeChange(AuthMode.PASSWORD)}
            className="text-purple-600 hover:underline"
            disabled={isLoading}
          >
            Back to password sign in
          </button>
        </div>
      )}
    </form>
  );
}
